import {
  REST_FALLBACK_SEC,
  SUPERSET_REST_SEC,
} from '@jackedlogs/shared';

import { resolveAdvance } from './timelineAdvance';
import { nextGroupMemberEntryIdx, type GroupMember } from './supersetNotebook';
import { type DraftEntry, rowIdOf } from './state/loggerDraftTypes';

// ONE decision per logged set (MX-5 slice 3b): where the focus goes next, and
// how long the rest before it lasts. The two used to be resolved apart —
// `resolveAdvance` ran in useTimelineLogger to move the focus AND again in
// useSetCyclePill to decide whether to arm a rest at all, while the superset
// round-robin ran only in the first. Two readings of "what comes next" that can
// disagree is the drift CONSOLIDAMENTO warns about, and slice 3b would have
// made it three (the rest length now depends on the same answer). So the
// answer is computed once, here, and everything downstream reads it.
//
// Deliberately free of i18n: the cue TEXT is built by the caller, which is
// where `t` and the exercise-name resolver already live. This file decides
// WHERE, not what it's called.
//
// Order matters. The superset round-robin is asked FIRST, because a group hop
// beats the ordinary "next set of this exercise" — that is what a notebook
// superset means (1A → 1B → 1A), and it was already the precedence in
// useTimelineLogger before this extraction.

export type RestTransition =
  /** Another set of the SAME exercise. The numbers are the 1-based set and the
   *  total, for the "→ Set N/T" caption.
   *
   *  `entryId` says WHICH row that "same exercise" is (TM-18a). It used to be
   *  absent because "the focus doesn't move" was trivially true: the whole
   *  logger froze for the countdown, so nothing else could move it either. With
   *  the freeze going away the athlete can wander off to correct a set and the
   *  rest-end resume has to know where to come back to — "stay here" is only an
   *  instruction if "here" is named. Same row identity as the `exercise` arm
   *  (LG-39): the ROW id, never the slug. */
  | { kind: 'set'; setNumber: number; total: number; entryId: string }
  /** Focus moves to another exercise. `viaGroupMember` says it is the next
   *  member of a superset rather than the next roster entry, which is the ONLY
   *  thing that shortens the rest (D9: 4 seconds is the time to change
   *  implement, not a recovery). */
  | {
      kind: 'exercise';
      entryIdx: number;
      /** WHERE THE CURSOR GOES — the target row's entryId (LG-39). */
      entryId: string;
      /** WHICH EXERCISE it is, for the cue's display name. Kept alongside
       *  entryId on purpose: they answer different questions and are only
       *  interchangeable while no exercise repeats in the workout. */
      exerciseId: string;
      viaGroupMember: boolean;
    }
  /** Nothing pending anywhere: every set on the sheet is logged. The focus
   *  blanks and the roster re-opens — there is no next set to point at.
   *
   *  ⚠️ `done` does NOT mean the workout is over, and the difference is the
   *  whole of TM-12. From v3.74.1 to 4.14.1 it armed no rest (inbox 1074fc37):
   *  "nothing pending" was read as "you have finished". But a set can always be
   *  added, and on a one-exercise session the FIRST set is already `done` — so
   *  that reading left the athlete with no rest exactly when they were about to
   *  train more. The gesture that really says "I have finished" is Save, and
   *  Save already tears the timer down (useSaveWorkout, both legs). So the rest
   *  is armed here like anywhere else (Antenore 2026-08-09). */
  | { kind: 'done' };

export function resolveRestTransition({
  entries,
  entryIdx,
  loggedSetIdx,
  groupMembers,
}: {
  /** PRE-dispatch entries: the just-logged set still reads pending, which is
   *  why both helpers below take `loggedSetIdx` and exclude it themselves. */
  entries: readonly DraftEntry[];
  entryIdx: number;
  loggedSetIdx: number;
  /** Members of the active exercise's group, already gated at ≥2 by
   *  groupMembersOf. Empty for a standalone exercise. */
  groupMembers: readonly GroupMember[];
}): RestTransition {
  const list = [...entries];
  const current = list[entryIdx];
  if (!current) return { kind: 'done' };

  if (groupMembers.length >= 2) {
    const nextIdx = nextGroupMemberEntryIdx(
      [...groupMembers],
      entryIdx,
      loggedSetIdx,
    );
    // `!== entryIdx` keeps the last set of a group from bouncing the focus
    // onto itself; when that happens we fall through to the ordinary
    // resolution below, which is what closes the round. Compared by ROW, not
    // by slug (LG-39): a circuit holding the same exercise twice would
    // otherwise read a hop to its twin as a bounce onto itself and swallow it.
    if (nextIdx !== null && nextIdx !== entryIdx) {
      const next = list[nextIdx];
      if (next) {
        return {
          kind: 'exercise',
          entryIdx: nextIdx,
          entryId: rowIdOf(next),
          exerciseId: next.exerciseId,
          viaGroupMember: true,
        };
      }
    }
  }

  const decision = resolveAdvance(list, entryIdx, loggedSetIdx);
  if (decision.kind === 'set') {
    return {
      kind: 'set',
      setNumber: decision.setNumber,
      total: decision.total,
      entryId: rowIdOf(current),
    };
  }
  if (decision.kind === 'exercise') {
    const target = list[decision.entryIdx];
    if (!target) return { kind: 'done' };
    return {
      kind: 'exercise',
      entryIdx: decision.entryIdx,
      entryId: rowIdOf(target),
      exerciseId: target.exerciseId,
      viaGroupMember: false,
    };
  }
  return { kind: 'done' };
}

/** How long the rest before that transition lasts. One chain, read once:
 *
 *   prescribedRestSec (SetPrescription.restSec — THIS set's own rest, HI-1)
 *   → superset hop → SUPERSET_REST_SEC (the implement change, D9)
 *   → exercise.defaultRestSec (catalog override, set per row)
 *   → profile.defaultRestSec (the family's global default)
 *   → REST_FALLBACK_SEC (compile-time floor of 90)
 *
 * The prescription goes FIRST because it is the only number in the chain
 * authored for this exact set: "30 seconds hard, 90 easy" is meaningless if
 * the 90 loses to a catalog default of 120. ⚠️ It therefore also beats the
 * superset hop — untested territory today, because the first HIIT cut is one
 * exercise with no group (interval-training.md §9.0/§9.2). When the circuit
 * slice lands, decide there whether a bout inside a group keeps its own rest
 * or takes the implement-change one; don't infer the answer from this line.
 *
 * The chain does NOT branch on where you are in the sheet: a rest is armed at
 * the end of every set, `done` included (TM-12, Antenore 2026-08-09 — see the
 * `done` arm of RestTransition for why the old zero was wrong).
 *
 * Returns 0 only for an exercise whose catalog default is 0, which is an
 * explicit per-row "no rest" and stays honoured; the caller reads 0 as "don't
 * arm anything", which is the behaviour useSetCyclePill already had. */
export function resolveRestSeconds({
  transition,
  prescribedRestSec,
  exerciseDefaultRestSec,
  profileDefaultRestSec,
}: {
  transition: RestTransition;
  /** SetPrescription.restSec for the set just logged, when the plan wrote
   *  one. 0 is a legitimate value ("no rest, go straight to the next bout")
   *  and must not fall through to the defaults, so this tests for undefined
   *  rather than truthiness. */
  prescribedRestSec?: number | null;
  exerciseDefaultRestSec: number | null | undefined;
  profileDefaultRestSec: number | null | undefined;
}): number {
  if (prescribedRestSec !== undefined && prescribedRestSec !== null) {
    return prescribedRestSec;
  }
  if (transition.kind === 'exercise' && transition.viaGroupMember) {
    return SUPERSET_REST_SEC;
  }
  return exerciseDefaultRestSec ?? profileDefaultRestSec ?? REST_FALLBACK_SEC;
}
